import * as api from './api';

class WebCacheService {
  constructor() {
    // Cache configurations for different data types
    this.CACHE_CONFIGS = {
      // Phase 1: Core data
      AMENITIES: {
        ttl: 7 * 24 * 60 * 60 * 1000, // 7 days
        key: 'cache_amenities_v2',
        version: '2.0',
        backgroundRefresh: true,
        fallbackOnError: true
      },
      POIS: {
        ttl: 7 * 24 * 60 * 60 * 1000, // 7 days
        key: 'cache_pois_v2',
        version: '2.0',
        backgroundRefresh: true,
        fallbackOnError: true
      },
      
      // Phase 2: User-specific data
      FAVORITES: {
        ttl: 24 * 60 * 60 * 1000, // 24 hours
        key: 'cache_favorites_v2',
        version: '2.0',
        backgroundRefresh: true,
        fallbackOnError: true
      },
      PROFILE: {
        ttl: 24 * 60 * 60 * 1000, // 24 hours
        key: 'cache_profile_v2',
        version: '2.0',
        backgroundRefresh: true,
        fallbackOnError: true
      },
      MY_TRIPS: {
        ttl: 6 * 60 * 60 * 1000, // 6 hours
        key: 'cache_my_trips_v2',
        version: '2.0',
        backgroundRefresh: true,
        fallbackOnError: true
      },
      
      // Phase 3: Static-ish data
      PUBLIC_ITINERARIES: {
        ttl: 12 * 60 * 60 * 1000, // 12 hours
        key: 'cache_public_itineraries_v2',
        version: '2.0',
        backgroundRefresh: true,
        fallbackOnError: true
      },
      PUBLIC_TOURS: {
        ttl: 12 * 60 * 60 * 1000, // 12 hours
        key: 'cache_public_tours_v2',
        version: '2.0',
        backgroundRefresh: true,
        fallbackOnError: true
      },
      
      // Existing data with shorter TTL
      WEATHER: {
        ttl: 24 * 60 * 60 * 1000, // 24 hours
        key: 'cache_weather_v2',
        version: '2.0',
        backgroundRefresh: true,
        fallbackOnError: true
      },
      EXCHANGE_RATES: {
        ttl: 24 * 60 * 60 * 1000, // 24 hours
        key: 'cache_exchange_rates_v2',
        version: '2.0',
        backgroundRefresh: true,
        fallbackOnError: true
      }
    };

    // Event listeners for cache invalidation
    this.listeners = {};
  }

  /**
   * Subscribe to cache invalidation events
   */
  subscribe(cacheType, callback) {
    if (!this.listeners[cacheType]) {
      this.listeners[cacheType] = [];
    }
    this.listeners[cacheType].push(callback);
    
    // Return unsubscribe function
    return () => {
      this.listeners[cacheType] = this.listeners[cacheType].filter(cb => cb !== callback);
    };
  }

  /**
   * Emit cache invalidation event
   */
  emit(cacheType, action) {
    if (this.listeners[cacheType]) {
      this.listeners[cacheType].forEach(callback => {
        try {
          callback(action);
        } catch (error) {
          console.error('Error in cache listener:', error);
        }
      });
    }
  }

  /**
   * Generic cache-first data fetching with background refresh
   */
  async get(cacheType, fetchFunction) {
    const config = this.CACHE_CONFIGS[cacheType];
    if (!config) {
      console.error(`Unknown cache type: ${cacheType}`);
      return fetchFunction();
    }

    const cachedData = this.getCachedData(config);
    
    if (cachedData && !this.isExpired(cachedData, config)) {
      console.log(`💻 Cache HIT: ${cacheType} (fresh)`);
      
      // Background refresh if needed
      if (config.backgroundRefresh && this.needsBackgroundRefresh(cachedData, config)) {
        this.backgroundRefresh(cacheType, fetchFunction, config);
      }
      
      return cachedData.data;
    }

    // Cache miss or expired - fetch fresh data
    if (cachedData && config.fallbackOnError) {
      // We have stale data - try to fetch fresh, fallback to stale on error
      try {
        console.log(`🔄 Cache MISS: ${cacheType} - fetching fresh data`);
        const freshData = await fetchFunction();
        this.setCachedData(config, freshData);
        return freshData;
      } catch (error) {
        console.log(`💻 Cache FALLBACK: ${cacheType} - using stale data due to error`);
        return cachedData.data;
      }
    } else {
      // No cached data or no fallback - must fetch fresh
      console.log(`🔄 Cache MISS: ${cacheType} - fetching fresh data`);
      const freshData = await fetchFunction();
      this.setCachedData(config, freshData);
      return freshData;
    }
  }

  /**
   * Background refresh (non-blocking)
   */
  async backgroundRefresh(cacheType, fetchFunction, config) {
    try {
      console.log(`🔄 Background refresh: ${cacheType}`);
      const freshData = await fetchFunction();
      this.setCachedData(config, freshData);
      console.log(`✅ Background refresh complete: ${cacheType}`);
    } catch (error) {
      console.error(`❌ Background refresh failed: ${cacheType}`, error);
    }
  }

  /**
   * Check if cached data is expired
   */
  isExpired(cachedData, config) {
    const age = Date.now() - cachedData.timestamp;
    return age > config.ttl;
  }

  /**
   * Check if background refresh is needed (refresh when 75% of TTL has passed)
   */
  needsBackgroundRefresh(cachedData, config) {
    const age = Date.now() - cachedData.timestamp;
    return age > (config.ttl * 0.75);
  }

  /**
   * Set data in cache with metadata
   */
  setCachedData(config, data) {
    try {
      const cacheEntry = {
        data,
        timestamp: Date.now(),
        version: config.version || '1.0',
        accessTime: Date.now()
      };
      localStorage.setItem(config.key, JSON.stringify(cacheEntry));
    } catch (error) {
      console.error(`Error caching ${config.key}:`, error);
    }
  }

  /**
   * Update access time for cached data
   */
  updateAccessTime(config) {
    try {
      const cached = localStorage.getItem(config.key);
      if (cached) {
        const cachedData = JSON.parse(cached);
        cachedData.accessTime = Date.now();
        localStorage.setItem(config.key, JSON.stringify(cachedData));
      }
    } catch (error) {
      console.error(`Error updating access time for ${config.key}:`, error);
    }
  }

  /**
   * Invalidate specific cache
   */
  invalidate(cacheType) {
    const config = this.CACHE_CONFIGS[cacheType];
    if (!config) return;

    try {
      localStorage.removeItem(config.key);
      console.log(`🗑️ Invalidated cache: ${cacheType}`);
      
      // Emit invalidation event
      this.emit(cacheType, 'invalidated');
    } catch (error) {
      console.error(`Error invalidating ${cacheType}:`, error);
    }
  }

  /**
   * Invalidate multiple caches
   */
  invalidateMultiple(cacheTypes) {
    cacheTypes.forEach(type => this.invalidate(type));
  }

  /**
   * Clear all caches
   */
  clearAll() {
    const keys = Object.values(this.CACHE_CONFIGS).map(config => config.key);
    try {
      keys.forEach(key => localStorage.removeItem(key));
      console.log('🗑️ Cleared all caches');
      
      // Emit invalidation events for all cache types
      Object.keys(this.CACHE_CONFIGS).forEach(cacheType => {
        this.emit(cacheType, 'cleared');
      });
    } catch (error) {
      console.error('Error clearing all caches:', error);
    }
  }

  /**
   * Get cache status for debugging
   */
  getStatus() {
    const status = {};
    Object.entries(this.CACHE_CONFIGS).forEach(([type, config]) => {
      const cached = this.getCachedData(config);
      if (cached) {
        const age = Date.now() - cached.timestamp;
        status[type] = {
          exists: true,
          ageHours: Math.round(age / (1000 * 60 * 60) * 100) / 100,
          expired: this.isExpired(cached, config),
          needsRefresh: this.needsBackgroundRefresh(cached, config)
        };
      } else {
        status[type] = { exists: false };
      }
    });
    return status;
  }

  // Data fetching methods
  async getAmenities() {
    return this.get('AMENITIES', () => api.getAmenities());
  }

  async getPOIs() {
    return this.get('POIS', () => api.getPOIs());
  }

  async getPublicItineraries() {
    return this.get('PUBLIC_ITINERARIES', () => api.getAllPublicItineraries());
  }

  async getPublicTours() {
    return this.get('PUBLIC_TOURS', () => api.getPublicTours());
  }

  async getWeather() {
    return this.get('WEATHER', () => api.getWeather());
  }

  async getExchangeRates() {
    return this.get('EXCHANGE_RATES', () => api.getExchangeRates());
  }

  async getFavorites() {
    return this.get('FAVORITES', () => api.getUserFavorites());
  }

  async getProfile() {
    return this.get('PROFILE', () => api.getUserProfile());
  }

  async getMyTrips() {
    return this.get('MY_TRIPS', () => api.getItineraries());
  }

  // User action wrapper methods with automatic cache invalidation
  async addFavorite(poiId) {
    try {
      const result = await api.addFavorite(poiId);
      this.onUserAction('favorite_added');
      console.log('✅ Added favorite and invalidated cache');
      return result;
    } catch (error) {
      console.error('Error adding favorite:', error);
      throw error;
    }
  }

  async removeFavorite(poiId) {
    try {
      const result = await api.removeFavorite(poiId);
      this.onUserAction('favorite_removed');
      console.log('✅ Removed favorite and invalidated cache');
      return result;
    } catch (error) {
      console.error('Error removing favorite:', error);
      throw error;
    }
  }

  async addPOIToTrip(tripId, poiId, day = 1, hour = 12, duration = 1) {
    try {
      const result = await api.addPOIToTrip(tripId, poiId, day, hour, duration);
      this.onUserAction('trip_updated');
      console.log('✅ Added POI to trip and invalidated cache');
      return result;
    } catch (error) {
      console.error('Error adding POI to trip:', error);
      throw error;
    }
  }

  async createTrip(tripData) {
    try {
      const result = await api.createTrip(tripData);
      this.onUserAction('trip_created');
      console.log('✅ Created trip and invalidated cache');
      return result;
    } catch (error) {
      console.error('Error creating trip:', error);
      throw error;
    }
  }

  async updateTrip(tripId, tripData) {
    try {
      const result = await api.updateItinerary(tripId, tripData);
      this.onUserAction('trip_updated');
      console.log('✅ Updated trip and invalidated cache');
      return result;
    } catch (error) {
      console.error('Error updating trip:', error);
      throw error;
    }
  }

  async deleteTrip(tripId) {
    try {
      const result = await api.deleteItinerary(tripId);
      this.onUserAction('trip_updated');
      console.log('✅ Deleted trip and invalidated cache');
      return result;
    } catch (error) {
      console.error('Error deleting trip:', error);
      throw error;
    }
  }

  async updateProfile(profileData) {
    try {
      const result = await api.updateUserProfile(profileData);
      this.onUserAction('profile_updated');
      console.log('✅ Updated profile and invalidated cache');
      return result;
    } catch (error) {
      console.error('Error updating profile:', error);
      throw error;
    }
  }

  // Private methods
  getCachedData(config) {
    try {
      const cached = localStorage.getItem(config.key);
      if (!cached) return null;

      const cachedData = JSON.parse(cached);
      
      // Check version compatibility
      if (config.version && cachedData.version !== config.version) {
        console.log(`🔄 Cache version mismatch for ${config.key}, invalidating`);
        localStorage.removeItem(config.key);
        return null;
      }

      return cachedData;
    } catch (error) {
      console.error(`Error reading cache ${config.key}:`, error);
      return null;
    }
  }

  // Cache invalidation triggers
  onUserAction(action) {
    switch (action) {
      case 'favorite_added':
      case 'favorite_removed':
        this.invalidate('FAVORITES');
        break;
      case 'trip_created':
      case 'trip_updated':
      case 'trip_deleted':
        this.invalidate('MY_TRIPS');
        break;
      case 'profile_updated':
        this.invalidate('PROFILE');
        break;
      case 'poi_updated':
        this.invalidate('POIS');
        break;
      case 'amenity_updated':
        this.invalidate('AMENITIES');
        break;
      default:
        console.warn(`Unknown user action: ${action}`);
    }
  }

  /**
   * Force refresh of specific cache data
   */
  async forceRefresh(cacheType) {
    const config = this.CACHE_CONFIGS[cacheType];
    if (!config) {
      console.error(`Unknown cache type: ${cacheType}`);
      return;
    }

    // Invalidate first
    this.invalidate(cacheType);
    
    // Then fetch fresh data based on cache type
    switch (cacheType) {
      case 'FAVORITES':
        return this.getFavorites();
      case 'MY_TRIPS':
        return this.getMyTrips();
      case 'PROFILE':
        return this.getProfile();
      case 'AMENITIES':
        return this.getAmenities();
      case 'POIS':
        return this.getPOIs();
      case 'PUBLIC_ITINERARIES':
        return this.getPublicItineraries();
      case 'PUBLIC_TOURS':
        return this.getPublicTours();
      case 'WEATHER':
        return this.getWeather();
      case 'EXCHANGE_RATES':
        return this.getExchangeRates();
      default:
        console.warn(`No refresh method for cache type: ${cacheType}`);
    }
  }
}

// Export singleton instance
const cacheService = new WebCacheService();
export default cacheService; 